/**
 * PHARMAKON — Fournisseur ERP · portal shell (Module 00, Appendix E.1)
 *
 * Replaces the 398-line monolithic FournisseurPortal shell. A modular, routed,
 * theme + density aware ERP V3 layout: left sidebar (nav from config, gated +
 * badge counts), topbar (org name, env chip, density toggle, theme toggle,
 * notification bell, staff menu), and an <Outlet/> for nested routes.
 *
 * Comfortable density on chrome (DESIGN_DIRECTIVE §4). Tokens + primitives only:
 * no PORTAL_CSS injection, no hardcoded hex, no emoji chrome icons.
 */
import * as React from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  Icon,
  IconButton,
  Badge,
  Wordmark,
  Kbd,
} from "@/components/ui/primitives";
import { useAuthStore } from "@/stores/authStore.js";
import { useThemeStore, isDarkTheme } from "@/stores/themeStore.js";
import { SHELLS } from "@/config/ui.config.js";
import { FOURNISSEUR_NAV } from "./fournisseur.nav.config.js";
import NotificationBell from "./NotificationBell.jsx";
import NotificationDrawer from "./NotificationDrawer.jsx";
import { useFournisseurNotifStore } from "@/stores/fournisseur/fournisseurNotifStore.js";
import { useFournisseurReclamationStore } from "@/stores/fournisseur/fournisseurReclamationStore.js";
import PortalKeyboardLayer from "./PortalKeyboardLayer.jsx";
import {
  supplierRoleLabel,
  supplierCan,
} from "@/config/fournisseur.constants.js";

// ── helpers ──────────────────────────────────────────────────────────────────
function initials(user) {
  const parts = [user?.prenom, user?.nom].filter(Boolean);
  if (parts.length) return parts.map((p) => p[0]).join("").toUpperCase().slice(0, 2);
  return (user?.email?.[0] || "?").toUpperCase();
}

// ── Sidebar ──────────────────────────────────────────────────────────────────
function FournisseurSidebar({ role, badges }) {
  const collapsed = useThemeStore((s) => s.sidebarCollapsed);
  const toggle = useThemeStore((s) => s.toggleSidebar);

  return (
    <aside
      className="pk-sidebar row-span-2 bg-bg-alt border-r border-border-default flex flex-col"
      style={{ gridArea: "sidebar" }}
    >
      {/* Brand */}
      <div className="h-14 flex items-center px-4 border-b border-border-subtle gap-2 shrink-0">
        {!collapsed ? (
          <div className="flex flex-col leading-tight min-w-0 flex-1">
            <Wordmark size="md" />
            <span className="text-2xs text-text-muted font-medium truncate mt-0.5">
              Portail Fournisseur
            </span>
          </div>
        ) : (
          <span className="inline-flex w-7 h-7 rounded-md bg-accent items-center justify-center text-accent-fg font-bold mx-auto">
            P
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2.5">
        {FOURNISSEUR_NAV.map((group, gi) => {
          const visibleItems = group.items.filter(
            (it) => !it.capability || supplierCan(role, it.capability),
          );
          if (visibleItems.length === 0) return null;
          return (
            <div key={gi} className={cn("mb-4", collapsed && "mb-2")}>
              {!collapsed && group.label && (
                <div className="px-2.5 mb-1.5 mt-1 text-[11px] uppercase tracking-wider text-text-muted font-bold select-none">
                  {group.label}
                </div>
              )}
              <ul className="flex flex-col gap-0.5">
                {visibleItems.map((it) => {
                  const count = it.badge ? badges?.[it.badge] ?? 0 : 0;
                  return (
                    <li key={it.id}>
                      <NavLink
                        to={it.to}
                        end={it.end}
                        title={collapsed ? it.label : undefined}
                        className={({ isActive }) =>
                          cn(
                            "group relative w-full flex items-center gap-2.5 px-3 h-[42px] rounded-md text-[15px] font-semibold transition-colors duration-150",
                            isActive
                              ? "bg-[var(--accent-dim)] text-accent font-bold border-l-2 border-accent pl-[8px]"
                              : "text-text-default border-l-2 border-transparent hover:bg-surface-hover hover:text-text-strong",
                            collapsed && "justify-center px-0 border-l-0",
                          )
                        }
                      >
                        <Icon name={it.icon} size={19} className="shrink-0" />
                        {!collapsed && (
                          <>
                            <span className="flex-1 text-left truncate">{it.label}</span>
                            {it.module && (
                              <Icon name="lock" size={11} className="text-text-muted" />
                            )}
                            {count > 0 && (
                              <Badge tone="accent" size="sm">
                                {count > 99 ? "99+" : count}
                              </Badge>
                            )}
                          </>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-border-subtle shrink-0">
        <div className={cn("flex items-center gap-2 px-3 py-2", collapsed && "flex-col")}>
          <IconButton
            icon={collapsed ? "panel-left-open" : "panel-left-close"}
            label={collapsed ? "Étendre" : "Réduire"}
            onClick={toggle}
            size="sm"
          />
          {!collapsed && (
            <span className="ml-auto bdg gray text-[10px]">{supplierRoleLabel(role)}</span>
          )}
        </div>
      </div>
    </aside>
  );
}

// ── Topbar ───────────────────────────────────────────────────────────────────
function FournisseurTopbar({ user, role }) {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const density = useThemeStore((s) => s.density);
  const setDensity = useThemeStore((s) => s.setDensity);
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  const mobileNavOpen = useThemeStore((s) => s.mobileNavOpen);
  const toggleMobileNav = useThemeStore((s) => s.toggleMobileNav);
  const [menuOpen, setMenuOpen] = React.useState(false);

  const orgName =
    user?.fournisseur?.nom ||
    [user?.prenom, user?.nom].filter(Boolean).join(" ") ||
    user?.email ||
    "Fournisseur";

  const handleLogout = async () => {
    await logout?.();
    navigate("/login");
  };

  return (
    <header
      className="relative h-14 flex items-center gap-4 px-5 bg-bg-elevated border-b border-border-subtle"
      style={{ gridArea: "topbar" }}
    >
      {/* Mobile nav toggle — at ≤767px the rail leaves the grid and becomes an
          off-canvas drawer, so this is the only way to reach the portal nav. */}
      <IconButton
        className="pk-mobile-nav-toggle shrink-0"
        icon="menu"
        label="Ouvrir la navigation"
        aria-expanded={mobileNavOpen}
        onClick={toggleMobileNav}
      />

      {/* Org context */}
      <div className="flex items-center gap-2.5 min-w-0 shrink-0">
        <div className="w-8 h-8 rounded-md bg-accent-soft text-accent flex items-center justify-center shrink-0 border border-accent/15">
          <Icon name="building-2" size={16} />
        </div>
        <div className="min-w-0 hidden lg:flex flex-col leading-tight">
          <span className="text-sm font-semibold text-text-strong truncate max-w-[220px]">
            {orgName}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-text-muted font-medium">
            {supplierRoleLabel(role)}
          </span>
        </div>
      </div>

      {/* Environment chip */}
      <span className="hidden md:inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-2xs font-semibold border border-border-subtle bg-bg-sunken text-text-muted">
        <Icon name="circle-dot" size={11} className="text-accent" />
        Portail B2B
      </span>

      {/* Right cluster */}
      <div className="flex items-center gap-1 shrink-0 ms-auto">
        {/* Density toggle */}
        <IconButton
          icon={density === "compact" ? "rows-3" : "rows-2"}
          label={
            density === "compact"
              ? "Densité : compacte — passer en confortable"
              : "Densité : confortable — passer en compacte"
          }
          aria-pressed={density === "compact"}
          className={density === "compact" ? "text-accent bg-accent-soft hover:bg-accent-soft" : ""}
          onClick={() => setDensity(density === "compact" ? "comfortable" : "compact")}
        />

        {/* Theme toggle — Novera Pro Clair ↔ Sombre (the full choice, incl.
            « Automatique », lives in the display preferences). */}
        <IconButton
          icon={isDarkTheme(theme) ? "sun" : "moon"}
          label={
            isDarkTheme(theme)
              ? "Thème : sombre — passer en clair"
              : "Thème : clair — passer en sombre"
          }
          onClick={toggleTheme}
        />

        <span aria-hidden="true" className="w-px h-5 bg-border-subtle mx-1" />

        {/* Notifications */}
        <NotificationBell />

        <span aria-hidden="true" className="w-px h-5 bg-border-subtle mx-1" />

        {/* User menu */}
        <div className="relative" data-fs-user-menu>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2 h-9 pr-2 pl-1 rounded-md hover:bg-bg-sunken/60 transition-colors"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <span className="w-7 h-7 rounded-full bg-accent text-accent-fg text-2xs font-semibold flex items-center justify-center ring-1 ring-accent/30">
              {initials(user)}
            </span>
            <div className="text-left hidden lg:flex flex-col leading-tight">
              <span className="text-xs font-semibold text-text-strong whitespace-nowrap">
                {[user?.prenom, user?.nom].filter(Boolean).join(" ") || user?.email}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-text-muted">
                {supplierRoleLabel(role)}
              </span>
            </div>
            <Icon name="chevron-down" size={12} className="text-text-muted shrink-0" />
          </button>
          {menuOpen && (
            <UserMenu
              user={user}
              onClose={() => setMenuOpen(false)}
              onLogout={handleLogout}
            />
          )}
        </div>
      </div>
    </header>
  );
}

// One row of the user menu. Module scope, not nested inside UserMenu: a
// component declared in a render body is a new type on every render, so React
// tears the row down and rebuilds it instead of updating it. It only ever read
// its own props, so hoisting it needs no extra wiring.
function UserMenuItem({ icon, label, onClick, tone }) {
  return (
    <li>
      <button
        onClick={onClick}
        className={cn(
          "w-full flex items-center gap-2.5 px-3.5 h-8 text-sm hover:bg-surface-hover transition-colors",
          tone === "danger" ? "text-danger" : "text-text-default",
        )}
      >
        <Icon name={icon} size={14} className="shrink-0" />
        <span className="flex-1 text-left">{label}</span>
      </button>
    </li>
  );
}

function UserMenu({ user, onClose, onLogout }) {
  const navigate = useNavigate();
  React.useEffect(() => {
    const close = (e) => {
      if (!e.target?.closest?.("[data-fs-user-menu]")) onClose();
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [onClose]);

  return (
    <div
      data-fs-user-menu
      className="absolute right-0 top-full mt-1.5 w-64 bg-bg-elevated border border-border-subtle rounded-lg shadow-pop overflow-hidden z-30"
    >
      <div className="px-4 py-3 border-b border-border-subtle">
        <div className="text-sm font-medium text-text-strong">
          {[user?.prenom, user?.nom].filter(Boolean).join(" ") || user?.email}
        </div>
        <div className="text-2xs text-text-muted">{user?.email}</div>
      </div>
      <ul className="py-1.5">
        <UserMenuItem
          icon="sliders-horizontal"
          label="Préférences de notifications"
          onClick={() => {
            navigate("/portail-fournisseur/parametres/notifications");
            onClose();
          }}
        />
        <div className="my-1.5 border-t border-border-subtle" />
        <UserMenuItem
          icon="log-out"
          label="Se déconnecter"
          onClick={() => {
            onLogout();
            onClose();
          }}
          tone="danger"
        />
      </ul>
    </div>
  );
}

// ── Error boundary (French recovery panel) ───────────────────────────────────
class PortalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("[FournisseurPortal] render error", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center text-center px-6 py-24 min-h-[60vh]">
          <div className="w-14 h-14 rounded-full bg-danger-soft text-danger flex items-center justify-center mb-4">
            <Icon name="alert-triangle" size={24} />
          </div>
          <h2 className="text-lg font-semibold text-text-strong mb-1">
            Une erreur est survenue
          </h2>
          <p className="text-sm text-text-muted max-w-md mb-5">
            Le chargement de cette page a échoué. Vous pouvez recharger le portail.
          </p>
          <button className="btn secondary" onClick={() => window.location.reload()}>
            <Icon name="rotate-cw" size={14} />
            Recharger
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Layout ───────────────────────────────────────────────────────────────────
export default function FournisseurLayout() {
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  const collapsed = useThemeStore((s) => s.sidebarCollapsed);
  const mobileNavOpen = useThemeStore((s) => s.mobileNavOpen);
  const closeMobileNav = useThemeStore((s) => s.closeMobileNav);
  const location = useLocation();

  const startPoll = useFournisseurNotifStore((s) => s.startPoll);
  const stopPoll = useFournisseurNotifStore((s) => s.stopPoll);
  const notifNonLues = useFournisseurNotifStore((s) => s.unread);
  const badgeReclamations = useFournisseurReclamationStore((s) => s.badge);
  const fetchBadgeReclamations = useFournisseurReclamationStore((s) => s.fetchBadge);

  // Start the unread-count poller for the lifetime of the portal.
  React.useEffect(() => {
    startPoll?.();
    return () => stopPoll?.();
  }, [startPoll, stopPoll]);

  // WP-36 (A-20) — the « Réclamations reçues » badge: read when the portal mounts and whenever the
  // feed's unread count moves (every pharmacy event on a réclamation notifies the org) — no poll of
  // its own; the réclamation pages re-read it after their writes.
  React.useEffect(() => {
    fetchBadgeReclamations();
  }, [notifNonLues, fetchBadgeReclamations]);
  const badges = React.useMemo(() => ({ reclamations: badgeReclamations }), [badgeReclamations]);

  // Navigating closes the mobile drawer; Escape dismisses it.
  React.useEffect(() => {
    closeMobileNav?.();
  }, [location.pathname, closeMobileNav]);

  React.useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeMobileNav?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileNavOpen, closeMobileNav]);

  return (
    <>
      <a href="#fs-main" className="skip-link">
        Aller au contenu
      </a>
      <div
        className="app-grid"
        data-shell={SHELLS.FOURNISSEUR}
        data-sidebar={collapsed ? "collapsed" : "expanded"}
        data-mobile-nav={mobileNavOpen ? "open" : "closed"}
      >
        <FournisseurSidebar role={role} badges={badges} />
        <FournisseurTopbar user={user} role={role} />
        <main
          id="fs-main"
          style={{ gridArea: "main" }}
          className="overflow-y-auto bg-bg"
        >
          <PortalErrorBoundary key={location.pathname}>
            <Outlet />
          </PortalErrorBoundary>
        </main>
      </div>
      {mobileNavOpen && (
        <button
          type="button"
          className="pk-nav-scrim"
          aria-label="Fermer la navigation"
          onClick={closeMobileNav}
        />
      )}
      <NotificationDrawer />
      <PortalKeyboardLayer />
    </>
  );
}
